/**
 * Feedback Answer Formatters — Pure utility functions for formatting
 * correct answers and user answers across all question types.
 *
 * Extracted from FeedbackDisplay.tsx (691→~300 lines) during CL-1 prep s62.
 * Co-located in practice/ domain (CL-1 s64).
 */

import type { Question, ScheduleEvent, MatchingPair } from '../../../content'
import { error as logError } from '../../../utils/logger'

// ── Number / Money Helpers (canonical source: src/utils/moneyFormatters.ts) ──
// R1-S5: Removed barrel re-exports — consumers should import directly from moneyFormatters
import {
  isFiniteNumber,
  normalizeCents,
  formatMoneyDisplay,
} from '../../../utils/moneyFormatters'
import {
  resolveCityName,
  resolveProvinceName,
  findNearestCity,
  distanceKm,
} from '../../../visuals/components/topoMap/nameResolver'
import riversData from '../../../content/atlases/nl/rivers.json'

// ── Map Question Helpers ─────────────────────────────────────

interface TargetPoint {
  cityId?: string
  name?: string
  lat: number
  lon: number
  label?: string
}

interface PlacedPoint {
  lat: number
  lon: number
}

function formatMapPlaceCitiesUser(
  question: Question,
  userAnswer: string,
): string {
  const data = (question.data ?? {}) as Record<string, unknown>
  const targets = (data.targetPoints as TargetPoint[] | undefined) ?? []
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 35
  let placements: PlacedPoint[] = []
  try {
    const parsed = JSON.parse(userAnswer)
    if (Array.isArray(parsed)) placements = parsed
  } catch {
    /* fall through */
  }
  if (targets.length === 0) return userAnswer || 'Geen antwoord'
  const lines = targets.map((t, i) => {
    const name = t.name ?? resolveCityName(t.cityId) ?? `Stad ${i + 1}`
    const p = placements[i]
    if (!p || typeof p.lat !== 'number' || typeof p.lon !== 'number') {
      return `❌ ${name} — niet geplaatst`
    }
    const dist = distanceKm(p, { lat: t.lat, lon: t.lon })
    if (dist <= tolKm) return `✅ ${name}`
    const distLabel = `${Math.round(dist)} km`
    return `❌ ${name} — ${distLabel} mis`
  })
  const okCount = lines.filter((l) => l.startsWith('✅')).length
  return `${lines.join(' • ')}  (${okCount}/${targets.length} goed)`
}

function formatMapPlaceCitiesCorrect(question: Question): string {
  const data = (question.data ?? {}) as Record<string, unknown>
  const targets = (data.targetPoints as TargetPoint[] | undefined) ?? []
  return targets
    .map((t, i) => t.name ?? resolveCityName(t.cityId) ?? `Stad ${i + 1}`)
    .join(', ')
}

function formatMapLocatePointUser(
  question: Question,
  userAnswer: string,
): string {
  const data = (question.data ?? {}) as Record<string, unknown>
  const mode = String(data.mode || 'free')
  const isSnapToDot =
    mode === 'snap-to-dot' || (data.correctCityId && !data.targetPoint)

  if (isSnapToDot) {
    if (!userAnswer) return 'Geen stad aangeklikt'
    return resolveCityName(userAnswer)
  }

  const target = data.targetPoint as TargetPoint | undefined
  const tolKm = typeof data.toleranceKm === 'number' ? data.toleranceKm : 30
  let click: PlacedPoint | null = null
  try {
    const parsed = JSON.parse(userAnswer)
    if (
      parsed &&
      typeof parsed.lat === 'number' &&
      typeof parsed.lon === 'number'
    ) {
      click = { lat: parsed.lat, lon: parsed.lon }
    }
  } catch {
    /* fall through */
  }
  if (!click) return 'Geen pin geplaatst'
  // Try to label the click via nearest known city
  const nearest = findNearestCity(click.lat, click.lon, 80)
  if (target) {
    const dist = distanceKm(click, { lat: target.lat, lon: target.lon })
    if (dist <= tolKm) {
      return nearest
        ? `Pin bij ${nearest.name} (goed!)`
        : 'Pin op de juiste plek'
    }
    const distLabel = `${Math.round(dist)} km mis`
    return nearest
      ? `Pin bij ${nearest.name} — ${distLabel}`
      : `Pin geplaatst — ${distLabel}`
  }
  return nearest ? `Pin bij ${nearest.name}` : 'Pin op de kaart'
}

// ── Map Locate Line (rivers) ────────────────────────────────

interface RiverEntry {
  id: string
  name: string
  path: [number, number][]
}

function getRivers(): RiverEntry[] {
  return (riversData?.rivers ?? []) as RiverEntry[]
}

function formatRiverLabel(name: string | undefined): string {
  if (!name) return 'Onbekende rivier'
  // Dutch rivers are conventionally prefixed with "de" — keep it natural.
  if (/^(de|het)\s/i.test(name)) return name
  return `de ${name}`
}

function resolveRiverNameById(riverId: string): string {
  const river = getRivers().find((r) => r.id === riverId)
  return formatRiverLabel(river?.name)
}

function findNearestRiverName(lat: number, lon: number): string | null {
  const rivers = getRivers()
  if (rivers.length === 0) return null
  let best: { name: string; dist: number } | null = null
  for (const river of rivers) {
    for (const [pLon, pLat] of river.path) {
      const d = distanceKm({ lat, lon }, { lat: pLat, lon: pLon })
      if (!best || d < best.dist) best = { name: river.name, dist: d }
    }
  }
  return best ? formatRiverLabel(best.name) : null
}

function formatMapLocateLineUser(
  question: Question,
  userAnswer: string,
): string {
  if (!userAnswer) return 'Geen rivier aangetikt'
  let click: PlacedPoint | null = null
  try {
    const parsed = JSON.parse(userAnswer)
    if (
      parsed &&
      typeof parsed.lat === 'number' &&
      typeof parsed.lon === 'number'
    ) {
      click = { lat: parsed.lat, lon: parsed.lon }
    }
  } catch {
    /* fall through */
  }
  if (!click) return 'Geen rivier aangetikt'
  const name = findNearestRiverName(click.lat, click.lon)
  return name ?? 'Rivier aangetikt op de kaart'
}

function formatMapLocateLineCorrect(question: Question): string {
  const data = (question.data ?? {}) as Record<string, unknown>
  const riverId = String(data.targetRiverId ?? '')
  if (!riverId) return 'Correcte rivier'
  return resolveRiverNameById(riverId)
}

function formatMapLocatePointCorrect(question: Question): string {
  const data = (question.data ?? {}) as Record<string, unknown>
  const mode = String(data.mode || 'free')
  const isSnapToDot =
    mode === 'snap-to-dot' || (data.correctCityId && !data.targetPoint)

  if (isSnapToDot) {
    return resolveCityName(String(data.correctCityId ?? ''))
  }

  const target = data.targetPoint as TargetPoint | undefined
  if (!target) return 'Correcte plek'
  if (target.label) return target.label
  if (target.cityId) return resolveCityName(target.cityId)
  if (target.name) return target.name
  return `${target.lat.toFixed(2)}, ${target.lon.toFixed(2)}`
}

// ── Correct Answer Formatting ────────────────────────────────

export function formatCorrectAnswer(question: Question): string {
  // ── Map question types (early return) ──
  if (question.questionType === 'map_locate_region') {
    const data = (question.data ?? {}) as Record<string, unknown>
    return resolveProvinceName(String(data.correctRegionId ?? ''))
  }
  if (question.questionType === 'map_locate_point') {
    return formatMapLocatePointCorrect(question)
  }
  if (question.questionType === 'map_locate_line') {
    return formatMapLocateLineCorrect(question)
  }
  if (question.questionType === 'map_place_cities') {
    return formatMapPlaceCitiesCorrect(question)
  }

  const isMoneyAssembler = question.questionType === 'money'
  const isVisualInteractive = [
    'bar_chart',
    'line_chart',
    'schedule_reading',
  ].includes(question.questionType || '')

  if (isMoneyAssembler) {
    const moneyConfig = question.data?.money
    if (!moneyConfig || typeof moneyConfig.targetCents !== 'number') {
      logError('FeedbackDisplay: Missing targetCents in question.data.money')
      return '€0,00'
    }
    const cents = normalizeCents(moneyConfig.targetCents)
    return cents === null ? '' : formatMoneyDisplay(cents)
  }

  if (isVisualInteractive) {
    const config = question.data || {}

    if (
      question.questionType === 'bar_chart' ||
      question.questionType === 'line_chart'
    ) {
      let chartData: Array<{ label: string; value: number }> = []
      if (Array.isArray(config.data) && config.data.length > 0) {
        chartData = config.data
      } else if (Array.isArray(config.labels) && Array.isArray(config.values)) {
        chartData = config.labels.map((label: string, i: number) => ({
          label,
          value: config.values?.[i] ?? 0,
        }))
      }
      const item = chartData[config.correctIndex]
      return item?.label || 'Correct antwoord'
    }

    if (question.questionType === 'schedule_reading') {
      const schedule = config.schedule || { events: [] }
      const correctCell = config.correctCell
      if (correctCell) {
        const event = schedule.events?.find(
          (e: ScheduleEvent) =>
            e.row === correctCell.row && e.col === correctCell.col,
        )
        if (event) {
          const colName = schedule.columns?.[correctCell.col] || ''
          const timeName = schedule.timeSlots?.[correctCell.row] || ''
          return `${event.content} (${colName}, ${timeName})`
        }
      }
      return 'Correct vakje'
    }
    return 'Correct antwoord'
  }

  if (question.questionType === 'ordering') {
    const config = question.data || {}
    const items = config.items || question.options || []
    const correctOrder = config.correctOrder
    if (correctOrder && items.length > 0) {
      return correctOrder.map((idx: number) => items[idx]).join(' → ')
    }
    return items.join(' → ')
  }

  if (question.questionType === 'fill_in') {
    const config = question.data || {}
    const accepted = config.acceptedAnswers
    if (accepted && accepted.length > 0) return accepted[0]
  }

  if (question.questionType === 'matching') {
    const config = question.data || {}
    const pairs = config.pairs
    if (pairs && pairs.length > 0) {
      return pairs.map((p: MatchingPair) => `${p.left} → ${p.right}`).join(', ')
    }
    return 'Correct antwoord'
  }

  if (question.questionType === 'text_highlight') {
    const config = question.data || {}
    // Collect correct segment texts
    if (config.segments && config.segments.length > 0) {
      const correctTexts = config.segments
        .filter((s: { isCorrect?: boolean }) => s.isCorrect === true)
        .map((s: { text: string }) => s.text)
      if (correctTexts.length > 0) return correctTexts.join(', ')
    }
    if (config.text && config.correctHighlights) {
      // Extract correct words from text using highlights
      const text: string = config.text
      const highlights: Array<{ start: number; end: number } | string> =
        config.correctHighlights
      const words: string[] = []
      for (const h of highlights) {
        if (typeof h === 'string') {
          words.push(h)
        } else if (
          typeof h === 'object' &&
          h !== null &&
          'start' in h &&
          'end' in h
        ) {
          const extracted =
            (h as { text?: string }).text || text.slice(h.start, h.end)
          words.push(extracted.trim())
        }
      }
      if (words.length > 0) return words.join(', ')
    }
    return 'Correct antwoord'
  }

  if (
    question.questionType === 'cijferend_rekenen' ||
    question.questionType === 'cijferend_delen'
  ) {
    const text = question.question || ''
    const clean = text.replace(/\./g, '').replace(/,/g, '.')
    const match = clean.match(/(\d+)\s*([+\-\u2212×✕*])\s*(\d+)/)
    if (match) {
      const a = parseInt(match[1])
      const opChar = match[2]
      const b = parseInt(match[3])
      const result =
        opChar === '+'
          ? a + b
          : opChar === '-' || opChar === '\u2212'
            ? a - b
            : a * b
      return result.toLocaleString('nl-NL')
    }
  }

  if (question.questionType === 'multiplication_grid') {
    const config = question.data || {}
    const hiddenCells = config.hiddenCells || []
    if (config.mode === 'cross_table') {
      const rows: number[] = config.rows || []
      const cols: number[] = config.cols || []
      return hiddenCells
        .map((cell: { row: number; col: number }) => {
          const r = rows[cell.row]
          const c = cols[cell.col]
          return r !== undefined && c !== undefined ? `${r}×${c}=${r * c}` : ''
        })
        .filter(Boolean)
        .join(', ')
    }
    const baseNumber = config.baseNumber || 1
    const range: number[] = config.range || [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]
    return hiddenCells
      .map((idx: number) => {
        const m = range[idx]
        return m !== undefined ? `${baseNumber}×${m}=${baseNumber * m}` : ''
      })
      .filter(Boolean)
      .join(', ')
  }

  return question.options?.[question.correctAnswer] ?? 'Correct antwoord'
}

// ── User Answer Formatting ───────────────────────────────────

export function formatUserAnswer(
  question: Question,
  userAnswer: string,
): string {
  // ── Map question types (early return) ──
  if (question.questionType === 'map_locate_region') {
    if (!userAnswer) return 'Geen provincie aangeklikt'
    return resolveProvinceName(userAnswer)
  }
  if (question.questionType === 'map_locate_point') {
    return formatMapLocatePointUser(question, userAnswer)
  }
  if (question.questionType === 'map_locate_line') {
    return formatMapLocateLineUser(question, userAnswer)
  }
  if (question.questionType === 'map_place_cities') {
    return formatMapPlaceCitiesUser(question, userAnswer)
  }

  if (question.questionType === 'ordering') {
    const config = question.data || {}
    const items = config.items || question.options || []
    if (items.length > 0 && userAnswer && userAnswer.includes(',')) {
      try {
        const indices = userAnswer.split(',').map(Number)
        if (
          indices.every((i: number) => !isNaN(i) && i >= 0 && i < items.length)
        ) {
          return indices.map((i: number) => items[i]).join(' → ')
        }
      } catch {
        /* fallthrough */
      }
    }
  }

  if (question.questionType === 'matching') {
    const config = question.data || {}
    const pairs = config.pairs
    if (pairs && pairs.length > 0 && userAnswer) {
      try {
        const userPairs: [number, number][] = JSON.parse(userAnswer)
        if (Array.isArray(userPairs) && userPairs.length > 0) {
          return userPairs
            .map(([leftIdx, rightIdx]) => {
              const left = pairs[leftIdx]?.left ?? '?'
              const right = pairs[rightIdx]?.right ?? '?'
              return `${left} → ${right}`
            })
            .join(', ')
        }
      } catch {
        /* fallthrough */
      }
    }
    return 'Geen antwoord'
  }

  return userAnswer || ''
}

// ── Money Token Parsing ──────────────────────────────────────

export interface MoneyToken {
  valueCents: number
  count: number
}

export function parseMoneyTokens(userAnswer: string): MoneyToken[] | null {
  try {
    const parsed = JSON.parse(userAnswer)
    if (!Array.isArray(parsed)) return null
    if (
      !parsed.every(
        (t) => isFiniteNumber(t?.valueCents) && isFiniteNumber(t?.count),
      )
    )
      return null
    return parsed
  } catch {
    return null
  }
}
